import {
  IconList,
  LinkButton,
  ResponsiveImage,
  SubsectionTitle,
} from '@sleepstation/components'
import { LinkBase } from '@sleepstation/components-base'
import { CopyParagraph } from '@sleepstation/components-copy'
import { BreakingLayout, VLayout } from '@sleepstation/components-layouts'
import { CopyRenderer } from '@sleepstation/components-markdown-renderer'
import {
  DIRECTION,
  LAYOUT_CHILD,
  LAYOUT_SPACING,
  SIZE,
} from '@sleepstation/const'
import {
  baseline,
  marginBottom,
  marginTop,
  mq,
  padding,
  themeColor,
  marginLeft,
  color,
} from '@sleepstation/style-api-helpers'
import { classStartsWith, outset } from '@sleepstation/style-mixins'
import { reduceObjIndexed } from '@sleepstation/utils'
import { reversedTheme } from '@sleepstation/themes-public-site'
import PropTypes from 'prop-types'
import { append } from 'ramda'
import React from 'react'
import styled, { ThemeProvider } from 'styled-components'
import starsCardVariantSvg from '../../../../../../../../content/images/illustrations/stars/starts-card-variant.svg'
import nhsLogoUrl from '../../../../../../../../content/images/illustrations/cards/nhs-logo.svg'

// -----------------------------------------------------------------------------
// Utils
// -----------------------------------------------------------------------------

const factory = (name, config) => {
  if (name === `bullets`) {
    return (
      <IconList
        shouldCenterRow={false}
        iconName={config.icon.name}
        iconType={config.icon.type}
        values={config.values}
        baselines={[1]}
      />
    )
  }

  throw new Error(`[DualColumnCtaCard] secondary no recognised variant`)
}

const renderSecondary = reduceObjIndexed((acc, [name, config]) => {
  const component = factory(name, config)
  return append(component, acc)
}, {})

// -----------------------------------------------------------------------------
// Sub-components
// -----------------------------------------------------------------------------

const Title = styled(SubsectionTitle)``

const Wrapper = styled(LinkBase).attrs(({ id }) => ({
  'data-cy': `${id}-card`,
}))`
  text-decoration: none;
`

const Layout = styled(VLayout).attrs(({ id }) => ({
  spacing: [
    LAYOUT_SPACING.X_LARGE,
    LAYOUT_SPACING.X_LARGE,
    LAYOUT_SPACING.LARGE,
  ],
  'data-cy': `${id}-card`,
}))`
  overflow: hidden;
  position: relative;
  ${padding([1, 1, 1.25])}
  ${outset(SIZE.LARGE, `background`)}
  background: url(${starsCardVariantSvg}), linear-gradient(${themeColor(
    `backgroundStart`
  )}, ${themeColor(`backgroundEnd`)});
  background-position:
    top right,
    top left;
  background-repeat: no-repeat;
  background-size: 231px, 100%;

  /* Add white background to icons */
  ${classStartsWith(`Icon__`)} {
    &::before {
      content: '';
      display: block;
      position: absolute;
      width: 12px;
      height: 12px;
      left: 3px;
      top: 3px;
      background-color: white;
      border-radius: 40%;
    }

    svg {
      position: relative;
      z-index: 1;
    }
  }
`

const Body = styled(BreakingLayout).attrs(({ id }) => ({
  spacing: [LAYOUT_SPACING.MEDIUM, LAYOUT_SPACING.MEDIUM, LAYOUT_SPACING.LARGE],
  direction: [DIRECTION.VERTICAL, DIRECTION.VERTICAL, DIRECTION.HORIZONTAL],
  child: [LAYOUT_CHILD.FIGHT, LAYOUT_CHILD.FIGHT, LAYOUT_CHILD.EQUAL],
  'data-cy': `${id}-card`,
}))``

const Primary = styled(VLayout).attrs({
  spacing: LAYOUT_SPACING.LARGE,
})`
  ${CopyParagraph}:not(:first-child) {
    margin-top: 0.5rem !important;
  }

  ${CopyParagraph} {
    margin-bottom: 0.5rem;
  }

  ${mq(`mediumUp`)} {
    flex: 0 1 55%;
    width: 55%;
  }
`

const Secondary = styled(VLayout).attrs({
  spacing: LAYOUT_SPACING.LARGE,
})`
  ${mq(`mediumUp`)} {
    flex: 0 1 35%;
    width: 35%;
  }
`

const Header = styled(VLayout).attrs({
  as: `header`,
  spacing: LAYOUT_SPACING.MEDIUM,
})`
  ${CopyParagraph} {
    ${baseline(1)}
    ${marginTop(0)}
  }
`

const TitleSpaced = styled(Title)`
  ${marginTop(0.6)}

  ${mq(`mediumUp`)} {
    margin-top: 0;
  }
`

const LinkPrologue = styled(CopyParagraph)`
  ${marginBottom(0.5)}
  ${color(`highlight`)}
  ${baseline(1.1)}
  font-weight: bold;
  text-align: center;

  ${mq(`mediumUp`)} {
    display: none;
  }
`

const LinkPrologueMup = styled(CopyParagraph)`
  display: none;
  ${marginTop(0.75)}
  ${marginBottom(0.75)}
  ${color(`highlight`)}
  ${baseline(1.1)}
  font-weight: bold;

  ${mq(`mediumUp`)} {
    display: block;
  }
`

const Footer = styled(VLayout).attrs({
  as: `footer`,
  spacing: LAYOUT_SPACING.MEDIUM,
})`
  > ${classStartsWith(`LinkButton__`)} {
    width: 100%;

    ${mq(`smallUp`)} {
      max-width: 300px;
    }
  }
`

const HeaderImageWrapper = styled.div`
  > * {
    &:first-child {
      width: 57px;

      ${mq(`mediumUp`)} {
        display: none;
      }
    }
  }
`

const TertiaryImageWrapper = styled.div`
  > * {
    &:first-child {
      width: 93px;
      display: none;
      margin-right: auto;

      ${mq(`mediumUp`)} {
        display: block;
      }
    }
  }
`

const ImageSmallWrapper = styled.div`
  width: calc(100% + 50px);
  ${marginLeft([-1, -1, -1.25])}
  ${marginTop(1)}
  ${marginBottom(-1)}

  ${mq(`mediumUp`)} {
    display: none;
  }
`

const ImageWrapper = styled.div`
  display: none;

  > * {
    margin-left: auto;
    width: 40%;
  }
  ${marginBottom([-1, -1, -1.25])}
  ${marginTop(-2)}

  ${mq(`mediumUp`)} {
    display: block;
  }
`

// -----------------------------------------------------------------------------
// Component
// -----------------------------------------------------------------------------

const DualColumnCtaCard = ({
  title,
  id,
  primary,
  secondary,
  image,
  link,
  shouldShowLink,
  shouldShowNhsLogo,
}) => {
  const linkHasPrologue = `prologue` in link

  return (
    <ThemeProvider theme={reversedTheme}>
      <Wrapper href={link.url} forceOpenInSameWindow>
        <Layout id={id}>
          <Body>
            <Primary>
              <Header>
                {shouldShowNhsLogo && (
                  <HeaderImageWrapper>
                    <ResponsiveImage
                      aspectRatio={57 / 23}
                      src={nhsLogoUrl}
                      alt="NHS service provider"
                    />
                  </HeaderImageWrapper>
                )}
                <TitleSpaced>{title}</TitleSpaced>
                <CopyRenderer htmlAst={primary.bodyAst} />
              </Header>
            </Primary>
            <Secondary>
              {renderSecondary(secondary)}
              {linkHasPrologue && (
                <LinkPrologueMup>{link.prologue}</LinkPrologueMup>
              )}

              {shouldShowNhsLogo && (
                <TertiaryImageWrapper>
                  <ResponsiveImage
                    aspectRatio={57 / 23}
                    src={nhsLogoUrl}
                    alt="NHS service provider"
                  />
                </TertiaryImageWrapper>
              )}
            </Secondary>
          </Body>
          <Footer>
            {shouldShowLink && (
              <React.Fragment>
                {linkHasPrologue && (
                  <LinkPrologue>{link.prologue}</LinkPrologue>
                )}
                <LinkButton as="div" size={SIZE.SMALL} data-cy="card-next-link">
                  {link.label}
                </LinkButton>
              </React.Fragment>
            )}
            <ImageSmallWrapper>
              <ResponsiveImage
                aspectRatio={image.aspectRatio}
                src={image.url}
                alt={image.alt}
              />
            </ImageSmallWrapper>
            <ImageWrapper>
              <ResponsiveImage
                aspectRatio={image.aspectRatio}
                src={image.url}
                alt={image.alt}
              />
            </ImageWrapper>
          </Footer>
        </Layout>
      </Wrapper>
    </ThemeProvider>
  )
}

DualColumnCtaCard.propTypes = {
  title: PropTypes.string.isRequired,
  id: PropTypes.string.isRequired,
  primary: PropTypes.object.isRequired,
  secondary: PropTypes.object.isRequired,
  link: PropTypes.object.isRequired,
  shouldShowLink: PropTypes.bool,
  shouldShowNhsLogo: PropTypes.bool,
  image: PropTypes.object.isRequired,
}

DualColumnCtaCard.defaultProps = {
  shouldShowLink: true,
  shouldShowNhsLogo: false,
}

export default DualColumnCtaCard
